import { logger } from '@adornis/base/logging.js';
import { registerSubscription } from '@adornis/baseql/metadata/register.js';
import { getCollection } from '@adornis/baseql/server/collections.js';
import { AdornisFile } from '@adornis/file-utils/db/files.js';
import { getLLMRouter } from '../llm/llm-gate.js';
import { ExpectedError } from '@adornis/validation/errors/ExpectedError.js';
import { jsonrepair } from 'jsonrepair';
import { Observable } from 'rxjs';
import { type BoundingBoxContentType, LEGACY_CONTENT_TYPE } from '../../../shared/bounding-box-content.js';
import { ocrAgentDefaultTemperature, ocrAgentModel, ocrAgentRetryTemperature } from '../../../shared/models.js';
import { OCRProgress, OCRState } from '../../ocr-progress.js';
import { buildOCRPrompts } from '../../prompts.js';
import { imagesToBase64URLsWithSizeFallback } from '../../utils/transform-file.js';
import { TaskType } from '../task-types/task-main.js';
import { OcrExtractionType } from './extraction-modules/ocrExtractionTypes.js';

function extractContentAsString(rawContent: unknown): string {
  if (typeof rawContent === 'string') return rawContent;
  if (Array.isArray(rawContent)) {
    return rawContent
      .map((item: any) => (typeof item?.text === 'string' ? item.text : typeof item === 'string' ? item : ''))
      .join('')
      .trim();
  }
  return '';
}

function stripCodeFences(value: string): string {
  return value
    .replace(/^```json\s*/i, '')
    .replace(/^```\s*/i, '')
    .replace(/\s*```$/i, '')
    .trim();
}

interface OCRPayload {
  type: BoundingBoxContentType;
  answer: string;
  confidence: number;
  isValidJSON: boolean;
}

interface OCRParseResult {
  payload: OCRPayload;
  /** Set only when isValidJSON is false — the JSON.parse error, for the invalid_json retry prompt. */
  parseError?: string;
}

function parseOCRJsonPayload(rawText: string): OCRParseResult {
  const fallback: OCRPayload = {
    type: LEGACY_CONTENT_TYPE,
    answer: rawText?.trim() || '',
    confidence: 0,
    isValidJSON: false,
  };
  if (!rawText) return { payload: fallback };

  const stripped = stripCodeFences(rawText);
  let parsed: any;
  let parseError: string | undefined;
  try {
    parsed = JSON.parse(stripped);
  } catch (firstError) {
    try {
      parsed = JSON.parse(jsonrepair(stripped));
    } catch {
      parseError = firstError instanceof Error ? firstError.message : String(firstError);
      return { payload: fallback, parseError };
    }
  }

  try {
    const rawType = typeof parsed?.type === 'string' ? parsed.type.trim().toLowerCase() : '';
    const explicitType: BoundingBoxContentType | null =
      rawType === 'image' || rawType === 'text' || rawType === 'both' ? (rawType as BoundingBoxContentType) : null;
    const rawAnswer = typeof parsed?.answer === 'string' ? parsed.answer : '';

    let type: BoundingBoxContentType;
    let answer: string;
    if (explicitType === null) {
      // Model gave no usable type → safe fallback to "both". Never auto-"image": a box's type
      // cannot be changed in the UI, so a wrong "image" would permanently hide its text.
      type = LEGACY_CONTENT_TYPE;
      answer = rawAnswer;
    } else if (explicitType === 'image') {
      type = 'image';
      answer = '';
    } else if (rawAnswer.trim().length === 0) {
      // Explicit text/both classification but nothing was transcribed:
      //   - "both" with no text is effectively just an image (acceptable downgrade),
      //   - "text" with no text is likely an OCR miss → keep it safe as "both" (image + editable text).
      type = explicitType === 'both' ? 'image' : LEGACY_CONTENT_TYPE;
      answer = explicitType === 'both' ? '' : rawAnswer;
    } else {
      type = explicitType;
      answer = rawAnswer;
    }

    const confidenceRaw = Number(parsed?.confidence ?? 0);
    const confidence = Number.isFinite(confidenceRaw) ? Math.max(0, Math.min(1, confidenceRaw)) : 0;
    return { payload: { type, answer, confidence, isValidJSON: true } };
  } catch {
    return { payload: fallback };
  }
}

function isValidOCRResult(payload: OCRPayload): boolean {
  // Must have successfully parsed JSON with a valid confidence. An "image" region legitimately
  // has an empty answer (no transcription), so only require non-empty text for text/both.
  const hasValidConfidence =
    typeof payload?.confidence === 'number' &&
    Number.isFinite(payload.confidence) &&
    payload.confidence >= 0 &&
    payload.confidence <= 1;
  const hasValidAnswer =
    payload?.type === 'image' || (typeof payload?.answer === 'string' && payload.answer.trim().length > 0);
  return Boolean(payload?.isValidJSON) && hasValidConfidence && hasValidAnswer;
}

interface OCRFailureContext {
  category: 'invalid_json' | 'empty_answer';
  rawText?: string;
  parseError?: string;
}

// invalid_json replays the model's own prior output so it can locate the defect, but is scoped to
// "fix formatting only" — the content was already decided, so this doesn't invite re-deriving it.
// empty_answer never references the prior attempt, to avoid pressuring the model into inventing
// text just to satisfy a "you gave nothing" nudge; it only restates the classification rule.
function buildRetryUserPrompt(userPrompt: string, failure: OCRFailureContext): string {
  if (failure.category === 'invalid_json') {
    return `${userPrompt}

Your previous response was:
${failure.rawText}

That failed to parse as JSON${failure.parseError ? ` (${failure.parseError})` : ''}. Re-emit the exact same information as a single valid JSON object matching {"type": "image"|"text"|"both", "answer": string, "confidence": number} — no surrounding text, no code fences. Do not change your reading of the image, only fix the JSON formatting.`;
  }
  return `${userPrompt}

Only use "text" or "both" when you can transcribe actual legible characters. If there is no legible text, respond with "type": "image" and an empty answer.`;
}

async function runOCRWithValidation(
  fileLink: string,
  systemPrompt: string,
  userPrompt: string,
  extractionType?: OcrExtractionType,
  maxRetries = 1,
  timeoutMs = 100000, // 10 seconds
): Promise<{
  payload: OCRPayload;
  cost: number;
  temperature: number;
}> {
  let retryCount = 0;
  let lastCost = 0;
  let usedTemperature = 0;
  let failure: OCRFailureContext | null = null;

  while (retryCount <= maxRetries) {
    const router = getLLMRouter();
    const temperature = retryCount > 0 ? ocrAgentRetryTemperature : ocrAgentDefaultTemperature;
    usedTemperature = temperature;
    const effectiveUserPrompt = failure ? buildRetryUserPrompt(userPrompt, failure) : userPrompt;

    try {
      // Create a timeout promise
      const timeoutPromise = new Promise<never>((_, reject) => {
        setTimeout(() => {
          reject(new ExpectedError(`OCR timeout: LLM call exceeded ${timeoutMs}ms`));
        }, timeoutMs);
      });

      const responsePromise = router.chat.completions.create({
        model: ocrAgentModel,
        provider: 'openrouter',
        temperature,
        messages: [
          { role: 'system', content: systemPrompt },
          {
            role: 'user',
            content: [
              { type: 'text', text: effectiveUserPrompt },
              { type: 'image_url', image_url: { url: fileLink, detail: 'low' } },
            ],
          },
        ],
      });
      responsePromise.catch(err => {
        logger.debug({ err }, 'Background OCR responsePromise rejected after timeout or racing conditions settled.');
      });

      const response = await Promise.race([responsePromise, timeoutPromise]);

      const rawContent = response.choices[0]?.message.content;
      const contentText = extractContentAsString(rawContent);
      const { payload: parsedPayload, parseError } = parseOCRJsonPayload(contentText);
      const usageCost = Number((response.usage as any)?.cost ?? 0);
      logger.info({ usageCost: JSON.stringify(usageCost), context: 'COST_TRACKING' }, 'OCR Agent cost tracked');
      lastCost += usageCost;

      if (isValidOCRResult(parsedPayload)) {
        return { payload: parsedPayload, cost: lastCost, temperature: usedTemperature };
      }

      failure = parsedPayload.isValidJSON
        ? { category: 'empty_answer' }
        : { category: 'invalid_json', rawText: contentText, parseError };

      logger.warn(
        {
          attempt: retryCount + 1,
          failureCategory: failure.category,
          answerLength: parsedPayload.answer.length,
          confidence: parsedPayload.confidence,
          isValidJSON: parsedPayload.isValidJSON,
        },
        'OCR validation failed on attempt',
      );
      retryCount++;
    } catch (error) {
      const errorMsg = error instanceof Error ? error.message : String(error);
      if (errorMsg.includes('timeout')) {
        // Timeout occurred - return immediately without retrying
        logger.error({ attempt: retryCount + 1 }, 'OCR timeout on attempt. Returning empty result without retry.');
        return {
          payload: { type: LEGACY_CONTENT_TYPE, answer: '', confidence: 0, isValidJSON: false },
          cost: lastCost,
          temperature: usedTemperature,
        };
      }

      logger.error(
        { err: error, attempt: retryCount + 1 },
        'OCR agent call encountered an API or network error on attempt',
      );
      failure = null;
      retryCount++;
    }
  }

  // If all retries failed, return the last result anyway
  logger.warn(
    { totalAttempts: maxRetries + 1 },
    'OCR validation failed after all attempts. Returning fallback result.',
  );
  return {
    payload: { type: LEGACY_CONTENT_TYPE, answer: '', confidence: 0, isValidJSON: false },
    cost: lastCost,
    temperature: usedTemperature,
  };
}

export const ocrExtract = registerSubscription({
  operationName: 'ocrExtract',
  type: () => OCRProgress,
  params: [
    { name: 'fileId', type: () => String },
    { name: 'extractionType', type: () => String },
    { name: 'taskType', type: () => String },
  ],
  resolve: (fileId: string, extractionType?: string, taskType?: string) => () =>
    new Observable<OCRProgress>(subscriber => {
      void (async () => {
        try {
          const fileCollection = await getCollection(AdornisFile);
          const file = await fileCollection.findOne({ _id: fileId });
          if (!file) {
            subscriber.error(new Error(`File ${fileId} not found`));
            return;
          }
          subscriber.next(new OCRProgress({ status: OCRState.RUNNING, answer: 'Starting analysis...' }));
          // Bild konvertieren
          const fileLink = (
            await imagesToBase64URLsWithSizeFallback([file as AdornisFile], { ocrEnhance: true, logTag: '[OCR]' })
          )[0];
          if (!fileLink) {
            subscriber.error(new Error(`Failed to convert file ${fileId} to base64`));
            return;
          }
          const normalizedExtractionType = Object.values(OcrExtractionType).includes(
            extractionType as OcrExtractionType,
          )
            ? (extractionType as OcrExtractionType)
            : OcrExtractionType.GENERIC;
          const normalizedTaskType = Object.values(TaskType).includes(taskType as TaskType)
            ? (taskType as TaskType)
            : TaskType.DEFAULT;
          const { systemPrompt, userPrompt } = buildOCRPrompts(normalizedExtractionType, normalizedTaskType);
          const { payload: parsedPayload, cost: totalCost } = await runOCRWithValidation(
            fileLink,
            systemPrompt,
            userPrompt,
            normalizedExtractionType,
            1, // max 1 retry
          );
          const finalAnswer = JSON.stringify(parsedPayload);
          // 4. Ergebnis senden
          subscriber.next(
            new OCRProgress({
              status: OCRState.COMPLETED,
              answer: finalAnswer,
              cost: Number.isFinite(totalCost) ? totalCost : 0,
            }),
          );
        } catch (error) {
          logger.error({ err: error }, 'Error during OCR extraction');
          subscriber.next(new OCRProgress({ status: OCRState.ERROR, answer: String(error) }));
        } finally {
          subscriber.complete();
        }
      })();
    }),
});
